import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { Egg, MarketResponse, MarketShelf, Wallet } from '../lib/types';
import { Bar, Button, Cost, ErrorState, LoadingGrid } from '../components/ui';
import { EggArt } from '../components/EggArt';
import { formatDuration, titleCase } from '../lib/format';

/**
 * The market is six shelves everyone in the world shares, turning over on a
 * fixed clock. The countdown is the page's spine: it is what makes "buy the
 * Damp Egg now" a decision rather than a permanently available option.
 *
 * Every number here comes from the server. The client only ever sends a family
 * key — never a price.
 */
export function EggMarketPage() {
  const { me, setWallet, refresh } = useSession();
  const { push } = useToast();
  const state = useAsync<MarketResponse>(() => api.get('/eggs/market'));
  const [busy, setBusy] = useState<string | null>(null);

  const rotation = state.data?.rotation;
  const [remaining, setRemaining] = useState(0);
  const [rotating, setRotating] = useState(false);
  // The slot the shelves on screen belong to, so we can tell a *real* turnover
  // from a countdown that merely drifted past zero.
  const shownSlot = useRef<number | null>(null);

  useEffect(() => {
    if (!rotation) return;
    shownSlot.current = rotation.slot;
    setRemaining(rotation.secondsRemaining);
  }, [rotation?.slot, rotation?.secondsRemaining]);

  // Tick locally; the clock endpoint is the authority when it runs out.
  useEffect(() => {
    const timer = window.setInterval(() => {
      setRemaining((current) => (current <= 0 ? 0 : current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (remaining > 0 || !rotation) return;
    let cancelled = false;

    const check = async () => {
      // A backgrounded tab is nobody watching a countdown. The rotation is
      // server-authoritative and this effect re-runs when the tab comes back,
      // so skipping here loses nothing and stops an abandoned tab asking the
      // server a question every four seconds forever.
      if (document.hidden) return;
      try {
        const clock = await api.get<{ slot: number; secondsRemaining: number }>('/eggs/market/clock');
        if (cancelled) return;
        if (clock.slot !== shownSlot.current) {
          setRotating(true);
          state.reload();
          window.setTimeout(() => !cancelled && setRotating(false), 800);
        } else {
          setRemaining(clock.secondsRemaining);
        }
      } catch {
        /* the next tick tries again */
      }
    };

    const timer = window.setInterval(() => void check(), 4000);
    const onVisible = () => {
      if (!document.hidden) void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    void check();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [remaining > 0, rotation?.slot]);

  async function buy(shelf: MarketShelf, quantity: number) {
    setBusy(shelf.key);
    try {
      const result = await api.post<{ eggs: Egg[]; wasFree: boolean; wallet: Wallet }>('/eggs/buy', {
        familyKey: shelf.key,
        quantity,
      });
      setWallet(result.wallet);
      push(
        'reward',
        result.wasFree
          ? `Free ${shelf.name} collected! It's in your egg storage.`
          : `${quantity} x ${shelf.name} added to storage.`,
        '◔',
      );
      state.reload();
      void refresh();
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : 'Could not buy that egg.';
      push('error', message);
      // A stale tab buying a rotated-out family is the common case — refresh it.
      if (caught instanceof ApiError && caught.code === 'ROTATED_OUT') state.reload();
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const soon = remaining > 0 && remaining <= 60;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Egg Market</h1>
          <div className="page-head__sub">
            The shelves restock every {rotation?.rotationMinutes ?? 15} minutes, and every trainer
            sees the same ones. What's inside an egg is for you to find out.
          </div>
        </div>
        <div className="row" style={{ gap: 'var(--gap-sm)' }}>
          <Link className="btn" to="/journal">
            Journal
          </Link>
          <Link className="btn btn--go" to="/hatchery">
            Go to Hatchery
          </Link>
        </div>
      </div>

      <div className="pxframe rotation-bar">
        <div className="stack-sm" style={{ gap: 2 }}>
          <div className="field__label">Shelves change in</div>
          <div className={`rotation-clock ${soon ? 'rotation-clock--soon' : ''}`}>
            {formatClock(remaining)}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <Bar
            ratio={
              rotation ? 1 - remaining / Math.max(1, rotation.rotationMinutes * 60) : 0
            }
            variant="egg"
          />
        </div>
        <div className="tiny muted" style={{ maxWidth: 260 }}>
          {soon
            ? 'Almost gone — buy now if you want one of these.'
            : 'A family can appear at very different odds from shelf to shelf.'}
        </div>
      </div>

      {state.loading && !state.data ? (
        <LoadingGrid count={6} height={260} />
      ) : (
        <div className={`shelf ${rotating ? 'shelf--rotating' : ''}`}>
          {state.data?.shelves.map((shelf) => (
            <ShelfCard
              key={`${rotation?.slot}-${shelf.key}`}
              shelf={shelf}
              wallet={me?.wallet ?? null}
              busy={busy === shelf.key}
              onBuy={(quantity) => void buy(shelf, quantity)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ShelfCard({
  shelf,
  wallet,
  busy,
  onBuy,
}: {
  shelf: MarketShelf;
  wallet: Wallet | null;
  busy: boolean;
  onBuy: (quantity: number) => void;
}) {
  const balance =
    shelf.currency === 'pokeCoins' ? wallet?.pokeCoins ?? 0 : wallet?.friendshipTokens ?? 0;
  const free = shelf.isDailyFree && !shelf.freeClaimedToday;
  const affordable = free || balance >= shelf.price;
  const discount = shelf.price < shelf.basePrice;

  return (
    <div
      className={`pxframe pxframe--raised shelf-card ${shelf.isFeatured ? 'shelf-card--featured' : ''}`}
    >
      <div className="shelf-card__art">
        <EggArt appearance={shelf.appearance} label={shelf.name} />
      </div>

      <div className="stack-sm" style={{ flex: 1 }}>
        <div className="row row--between" style={{ alignItems: 'flex-start' }}>
          <h3>{shelf.name}</h3>
          <span className="chip">{titleCase(shelf.theme)}</span>
        </div>

        <p className="tiny muted" style={{ margin: 0 }}>
          {shelf.description}
        </p>

        <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
          <div className="stack-sm">
            <div className="row row--between tiny mono">
              <span>Journal</span>
              <Link to={`/journal/${shelf.key}`} className="tiny">
                {shelf.discovered} / {shelf.totalSpecies} known
              </Link>
            </div>
            <Bar ratio={shelf.discovered / Math.max(1, shelf.totalSpecies)} />
            <div className="tiny muted mono">~{formatDuration(shelf.hatchSteps * 45)} to hatch</div>
          </div>
        </div>
      </div>

      <div className="row row--between">
        <div>
          {free ? (
            <span className="mono" style={{ color: 'var(--leaf-dark)' }}>
              FREE TODAY
            </span>
          ) : shelf.isDailyFree ? (
            <span className="tiny muted">Come back tomorrow</span>
          ) : (
            <div className="stack-sm" style={{ gap: 0 }}>
              <Cost amount={shelf.price} currency={shelf.currency} />
              {discount && (
                <span className="tiny muted" style={{ textDecoration: 'line-through' }}>
                  {shelf.basePrice.toLocaleString()}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="row" style={{ gap: 4 }}>
          {!shelf.isDailyFree && (
            <Button
              size="sm"
              disabled={busy || balance < shelf.price * 5}
              onClick={() => onBuy(5)}
              title={`Buy 5 for ${(shelf.price * 5).toLocaleString()}`}
            >
              x5
            </Button>
          )}
          <Button
            variant={free ? 'go' : 'primary'}
            disabled={busy || (shelf.isDailyFree ? shelf.freeClaimedToday : !affordable)}
            loading={busy}
            onClick={() => onBuy(1)}
          >
            {shelf.isDailyFree
              ? shelf.freeClaimedToday
                ? 'Claimed'
                : 'Claim'
              : affordable
                ? 'Buy egg'
                : 'Too expensive'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** mm:ss — the countdown is read at a glance, so it stays fixed width. */
function formatClock(seconds: number): string {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  return `${String(minutes).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
}
